import { useState, useEffect, useRef } from "react";
import { ChevronDown, Check, X, Search } from "lucide-react";
import { cn } from "@/lib/utils";

interface ComboboxOption {
  value: string;
  label: string;
  description?: string;
}

interface ComboboxProps {
  value: string;
  onChange: (value: string) => void;
  options: ComboboxOption[];
  placeholder?: string;
  allowFreeText?: boolean; // If true, allows custom values not in the list (typing in main input)
  searchable?: boolean; // If true, shows search box in dropdown (main input is read-only)
  disabled?: boolean;
  className?: string;
}

export function Combobox({
  value,
  onChange,
  options,
  placeholder = "Select...",
  allowFreeText = false,
  searchable = false,
  disabled = false,
  className
}: ComboboxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [searchValue, setSearchValue] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Get display value for the main input
  const displayValue = (() => {
    const selectedOption = options.find(option => option.value === value);
    return selectedOption ? selectedOption.label : (allowFreeText ? value : "");
  })();

  // Update input value when external value changes (only for allowFreeText mode)
  useEffect(() => {
    if (allowFreeText) {
      const selectedOption = options.find(option => option.value === value);
      setInputValue(selectedOption ? selectedOption.label : value);
    }
  }, [value, options, allowFreeText]);

  // Filter options based on search value (for searchable mode) or input value (for allowFreeText mode)
  useEffect(() => {
    const searchTerm = searchable ? searchValue : inputValue;
    
    if (!searchTerm.trim()) {
      setFilteredOptions(options);
    } else {
      // For allowFreeText, don't filter if input matches selected option's label
      if (allowFreeText) {
        const selectedOption = options.find(option => option.value === value);
        if (selectedOption && inputValue === selectedOption.label) {
          setFilteredOptions(options);
          return;
        }
      }
      
      const filtered = options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        option.value.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
  }, [searchValue, inputValue, options, value, searchable, allowFreeText]);

  // Reset search when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchValue("");
    }
  }, [isOpen]);

  // Focus search input when dropdown opens (for searchable mode)
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 10);
    }
  }, [isOpen, searchable]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setIsFocused(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleInputChange = (newValue: string) => {
    if (!allowFreeText) return; // Don't allow typing if not in free text mode
    
    setInputValue(newValue);
    onChange(newValue);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleOptionSelect = (option: ComboboxOption) => {
    if (allowFreeText) {
      setInputValue(option.label);
    }
    onChange(option.value);
    setIsOpen(false);
    setSearchValue("");
  };

  const handleToggleDropdown = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
  };

  const handleClear = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (allowFreeText) {
      setInputValue("");
    }
    onChange("");
    setSearchValue("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsOpen(false);
    } else if (e.key === "Enter" && filteredOptions.length > 0) {
      handleOptionSelect(filteredOptions[0]);
    }
  };

  const showClear = value.trim() && !disabled;
  const hasOptions = filteredOptions.length > 0;

  // For non-free-text mode, the main input is read-only
  const isMainInputReadOnly = !allowFreeText;

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <div 
        className={cn(
          "flex border rounded-md bg-background transition-all",
          isFocused 
            ? "border-primary ring-2 ring-primary/20" 
            : "border-border",
          isMainInputReadOnly && "cursor-pointer"
        )}
        onClick={isMainInputReadOnly ? handleToggleDropdown : undefined}
      >
        <input
          ref={inputRef}
          type="text"
          value={allowFreeText ? inputValue : displayValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => {
            setIsFocused(true);
            if (allowFreeText && !isOpen) {
              setIsOpen(true);
            }
          }}
          onBlur={() => setIsFocused(false)}
          onKeyDown={handleKeyDown}
          className={cn(
            "w-full px-3 py-2 text-sm bg-transparent focus:outline-none rounded-l-md",
            isMainInputReadOnly && "cursor-pointer caret-transparent"
          )}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={isMainInputReadOnly}
          autoComplete="off"
        />
        
        <div className="flex">
          {showClear && (
            <button
              type="button"
              onMouseDown={handleClear}
              className="px-2 py-2 text-sm bg-transparent hover:bg-muted focus:outline-none transition-colors text-muted-foreground hover:text-foreground cursor-pointer"
              disabled={disabled}
            >
              <X className="w-4 h-4" />
            </button>
          )}
          
          <button
            type="button"
            onClick={handleToggleDropdown}
            className="px-3 py-2 text-sm bg-transparent hover:bg-muted focus:outline-none rounded-r-md transition-colors cursor-pointer"
            disabled={disabled}
          >
            <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-background border border-border rounded-md shadow-lg">
          {/* Search box in dropdown for searchable mode */}
          {searchable && (
            <div className="p-2 border-b border-border">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-border rounded bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                  placeholder="Search..."
                  autoComplete="off"
                />
              </div>
            </div>
          )}
          
          <div className="max-h-60 overflow-auto">
            {hasOptions ? (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className="px-3 py-2 text-sm hover:bg-muted cursor-pointer flex items-center justify-between transition-colors"
                  onMouseDown={() => handleOptionSelect(option)}
                >
                  <div>
                    <div className="font-medium">{option.label}</div>
                    {option.description && (
                      <div className="text-xs text-muted-foreground">{option.description}</div>
                    )}
                  </div>
                  {value === option.value && <Check className="w-4 h-4 text-primary" />}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                {allowFreeText ? "Type to create new entry" : "No matches found"}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
